/**
 * Optional language-model assist for meal parsing.
 *
 * OFF unless the user configures it. The on-device parser is the primary path
 * and works with no network; this layer exists for the cases where USDA's
 * filing conventions defeat keyword matching — "chicken breast" is filed as
 * "Chicken, broilers or fryers, breast, meat only, cooked, roasted", which no
 * amount of token scoring will find from the phrase a person actually types.
 *
 * The model is asked for two things per item: a USDA-style search phrase
 * (which is then resolved against the SAME bundled database, so macros still
 * come from public-domain data rather than from the model's memory) and an
 * estimated gram weight, used only when the database has no portion for the
 * amount stated.
 *
 * Two providers:
 *   - 'anthropic': the official SDK, run in the browser with the user's own
 *     API key, which never leaves the device except to api.anthropic.com.
 *   - 'openai-compatible': any local server speaking the chat-completions
 *     shape (LM Studio, Ollama, llama.cpp) — the path for the Mac Studio.
 *
 * Nothing here is sent unless the user has turned the feature on, and the
 * text sent is exactly the meal description they typed.
 */
import { z } from 'zod';
import type { Settings } from './schema';

export const LLM_DEFAULT_MODEL = 'claude-opus-5';

const ItemSchema = z.object({
  /** Short display name in plain words, e.g. "chicken breast". */
  name: z.string(),
  /** Amount as stated, or a sensible single serving if none was stated. */
  qty: z.number(),
  /** Unit as stated ("oz", "cup", "slice"), or null for a bare count. */
  unit: z.string().nullable(),
  /** Estimated grams for the stated amount. */
  grams: z.number(),
  /** USDA FoodData Central style search phrase, lowercase, no brand names. */
  query: z.string(),
  /** True when the amount was not stated and had to be assumed. */
  assumed: z.boolean(),
});
const MealSchema = z.object({ items: z.array(ItemSchema) });

export type LlmItem = z.infer<typeof ItemSchema>;

const SYSTEM = `You turn a short description of a meal into structured items.
For each food, give a "query" phrased the way the USDA FoodData Central
database names foods: head noun first, then preparation, e.g. "chicken breast
cooked roasted", "rice white cooked", "salmon atlantic cooked", "bread whole
wheat toasted", "coffee brewed". Never include brand names in query. Estimate
grams for the stated amount using ordinary serving weights. If no amount is
stated, assume one typical serving and set assumed to true. Do not invent
foods that were not mentioned. Return only the structured result.`;

function needsConfig(llm: Settings['llm']): string | null {
  if (!llm.enabled) return 'Language-model assist is off.';
  if (llm.provider === 'anthropic' && !llm.apiKey) return 'Add an API key in Settings.';
  if (llm.provider === 'openai-compatible' && !llm.baseUrl) return 'Add the server address in Settings.';
  return null;
}

/** Parse a meal description with the configured model. Throws on any failure. */
export async function parseMealWithLlm(text: string, llm: Settings['llm']): Promise<LlmItem[]> {
  const problem = needsConfig(llm);
  if (problem) throw new Error(problem);

  if (llm.provider === 'anthropic') return viaAnthropic(text, llm);
  return viaOpenAiCompatible(text, llm);
}

async function viaAnthropic(text: string, llm: Settings['llm']): Promise<LlmItem[]> {
  // Loaded on demand so the app shell does not carry the SDK.
  const [{ default: Anthropic }, { zodOutputFormat }] = await Promise.all([
    import('@anthropic-ai/sdk'),
    import('@anthropic-ai/sdk/helpers/zod'),
  ]);
  const client = new Anthropic({
    apiKey: llm.apiKey!,
    // This is a single-user app running on the user's own phone with the
    // user's own key; there is no server to hide it behind by design.
    dangerouslyAllowBrowser: true,
    ...(llm.baseUrl ? { baseURL: llm.baseUrl } : {}),
  });

  const response = await client.messages.parse({
    model: llm.model || LLM_DEFAULT_MODEL,
    max_tokens: 2048,
    system: SYSTEM,
    output_config: { effort: 'low', format: zodOutputFormat(MealSchema) },
    messages: [{ role: 'user', content: text }],
  });

  if (response.stop_reason === 'refusal') throw new Error('The model declined to parse that.');
  if (!response.parsed_output) throw new Error('The model returned nothing usable.');
  return response.parsed_output.items;
}

async function viaOpenAiCompatible(text: string, llm: Settings['llm']): Promise<LlmItem[]> {
  const base = llm.baseUrl!.replace(/\/+$/, '');
  const res = await fetch(`${base}/chat/completions`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      ...(llm.apiKey ? { authorization: `Bearer ${llm.apiKey}` } : {}),
    },
    body: JSON.stringify({
      model: llm.model || 'default',
      temperature: 0,
      response_format: { type: 'json_object' },
      messages: [
        { role: 'system', content: `${SYSTEM}\nRespond with JSON: {"items":[{"name","qty","unit","grams","query","assumed"}]}` },
        { role: 'user', content: text },
      ],
    }),
  });
  if (!res.ok) throw new Error(`Model server returned ${res.status}`);
  const body = (await res.json()) as { choices?: { message?: { content?: string } }[] };
  const content = body.choices?.[0]?.message?.content;
  if (!content) throw new Error('Model server returned no content.');

  let parsed: unknown;
  try {
    parsed = JSON.parse(content);
  } catch {
    throw new Error('Model server returned invalid JSON.');
  }
  const result = MealSchema.safeParse(parsed);
  if (!result.success) throw new Error('Model server returned an unexpected shape.');
  return result.data.items;
}
